import { Box, Typography } from "@mui/material";
import { ColumnConfig } from "../DataTable";
import { colors } from "../../theme/colors";
import { DetailsLink } from "./DashboardPatientsList.styled";
import { Patient } from "../../services/patientsService";

interface TableConfigProps {
  activeTab: 1 | 2 | 3;
  showProviderColumn: boolean;
  onPatientClick: (patientId: number) => void;
  onProviderClick?: (providerId: number) => void;
  onTreatmentPlanClick?: (treatmentPlanId: number) => void;
  formatDate: (dateString: string) => string;
}

// Helper to get initials from name
const getInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName?.charAt(0) || "";
  const last = lastName?.charAt(0) || "";
  return (first + last).toUpperCase() || "?";
};

// Helper to format full name
const formatFullName = (firstName?: string, lastName?: string): string => {
  return [firstName, lastName].filter(Boolean).join(" ") || "Unknown";
};

export const createTableColumns = ({
  showProviderColumn,
  onPatientClick,
  onProviderClick,
  onTreatmentPlanClick,
  formatDate,
}: TableConfigProps): ColumnConfig<Patient>[] => {
  const columns: ColumnConfig<Patient>[] = [];

  // Provider column - only for Manager/Assistant roles
  if (showProviderColumn) {
    columns.push({
      key: "ProviderName",
      title: "Provider",
      sortable: true,
      render: (_value, row) => {
        const fullName = formatFullName(
          row.providerFirstName,
          row.providerLastName,
        );
        const initials = getInitials(
          row.providerFirstName,
          row.providerLastName,
        );

        const handleClick =
          row.ownerUserId && onProviderClick
            ? () => onProviderClick(row.ownerUserId)
            : undefined;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {row.providerAvatarUrl ? (
              <Box
                component="img"
                src={row.providerAvatarUrl}
                alt={fullName}
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "#E8F4F8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: colors.primary.main,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Box>
            )}
            <Typography
              className={handleClick ? "clickable" : undefined}
              onClick={handleClick}
            >
              {fullName}
            </Typography>
          </Box>
        );
      },
    });
  }

  // Patient Name column
  columns.push({
    key: "Name",
    title: "Patient",
    sortable: true,
    render: (_value, row) => {
      const fullName = formatFullName(
        row.firstName,
        row.lastName,
      );

      return (
        <Typography
          className="clickable"
          onClick={() => onPatientClick(row.id)}
        >
          {fullName}
        </Typography>
      );
    },
  });

  // Treatment Plan Name column
  columns.push({
    key: "treatmentPlan",
    title: "Treatment Plan",
    render: (_, row) => {
      if (!row.latestTreatmentPlanId) {
        return <Typography>No plan available</Typography>;
      }

      return (
        <Typography
          className="clickable"
          onClick={() =>
            onTreatmentPlanClick && onTreatmentPlanClick(row.latestTreatmentPlanId!)
          }
        >
          {row.latestTreatmentPlanName}
        </Typography>
      );
    },
  });

  // Date Created column
  columns.push({
    key: "createdAt",
    title: "Date Created",
    sortable: true,
    render: (_, row) => {
      const createdAt = row.latestTreatmentPlanCreatedAt;
      return (
        <Typography>
          {createdAt ? formatDate(createdAt) : "—"}
        </Typography>
      );
    },
  });

  // Action column
  columns.push({
    key: "action",
    title: "Action",
    render: (_, row) => {
      if (!row.latestTreatmentPlanId) {
        return <Typography>—</Typography>;
      }

      return (
        <DetailsLink
          onClick={() =>
            onTreatmentPlanClick && onTreatmentPlanClick(row.latestTreatmentPlanId!)
          }
        >
          View Details
        </DetailsLink>
      );
    },
  });

  return columns;
};
